'use client';

/**
 * Celebration effects — confetti, floating reward crumbs and the star burst.
 *
 * Confetti is drawn on a single <canvas> rather than as DOM nodes: 140 animated
 * divs will drop frames on a school Chromebook, one canvas will not. The whole
 * thing is a no-op when the user has asked for reduced motion.
 */
import React, { useEffect, useRef, useState } from 'react';

const COLORS = ['#6D4AFF', '#2E8BFF', '#12B981', '#F5A524', '#F43F5E', '#EC4899', '#06B6D4'];

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  if (document.documentElement.getAttribute('data-kids-motion') === 'calm') return true;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

interface Piece {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rotation: number;
  spin: number;
  color: string;
}

export interface ConfettiProps {
  /** Bump this number to fire a fresh burst. */
  trigger: number;
  /** Roughly how many pieces. Scaled down on small screens. */
  count?: number;
  durationMs?: number;
}

export function Confetti({ trigger, count = 130, durationMs = 2600 }: ConfettiProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    if (!trigger) return;
    const canvas = canvasRef.current;
    if (!canvas || prefersReducedMotion()) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Cap the backing store at 2× so a 4K display does not render 8× the pixels.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = canvas.offsetWidth;
    const height = canvas.offsetHeight;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const total = width < 520 ? Math.round(count * 0.55) : count;
    let seed = trigger * 9301;
    const random = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };

    const pieces: Piece[] = Array.from({ length: total }, () => ({
      x: width / 2 + (random() - 0.5) * width * 0.55,
      y: height * 0.32 + (random() - 0.5) * 40,
      vx: (random() - 0.5) * 9,
      vy: random() * -11 - 4,
      size: 5 + random() * 7,
      rotation: random() * Math.PI * 2,
      spin: (random() - 0.5) * 0.28,
      color: COLORS[Math.floor(random() * COLORS.length)],
    }));

    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = elapsed / durationMs;
      ctx.clearRect(0, 0, width, height);

      if (progress >= 1) {
        cancelAnimationFrame(frameRef.current);
        return;
      }

      for (const piece of pieces) {
        piece.vy += 0.34; // gravity
        piece.vx *= 0.995; // drag
        piece.x += piece.vx;
        piece.y += piece.vy;
        piece.rotation += piece.spin;

        ctx.save();
        ctx.translate(piece.x, piece.y);
        ctx.rotate(piece.rotation);
        ctx.globalAlpha = Math.max(0, 1 - progress * 1.15);
        ctx.fillStyle = piece.color;
        // Rectangles read as paper; squashing height as it spins fakes 3-D.
        ctx.fillRect(-piece.size / 2, -piece.size / 4, piece.size, piece.size * 0.55);
        ctx.restore();
      }

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [trigger, count, durationMs]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 w-full h-full"
      style={{ zIndex: 40 }}
    />
  );
}

/* ─────────────────────────────────────────────────────── floating crumbs ── */

export interface Floater {
  id: number;
  text: string;
  color: string;
  x: number;
  y: number;
}

/**
 * Small "+12 XP" style labels that drift upward and fade. Rendered from a
 * queue so several can overlap without fighting each other.
 */
export function Floaters({ items }: { items: Floater[] }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {items.map((item) => (
        <span
          key={item.id}
          className="k-floater"
          style={{ left: `${item.x}%`, top: `${item.y}%`, color: item.color }}
        >
          {item.text}
        </span>
      ))}
    </div>
  );
}

/** Queue helper — owns the ids and the auto-expiry so callers stay simple. */
export function useFloaters() {
  const [items, setItems] = useState<Floater[]>([]);
  const nextId = useRef(1);

  const push = (text: string, color = 'var(--k-mango)', x = 50, y = 55) => {
    const id = nextId.current++;
    setItems((prev) => [...prev, { id, text, color, x, y }]);
    window.setTimeout(() => setItems((prev) => prev.filter((f) => f.id !== id)), 1000);
  };

  return { items, push };
}

/* ──────────────────────────────────────────────────────────── star burst ── */

export function StarRow({
  stars,
  size = 40,
  animate = false,
}: {
  stars: number;
  size?: number;
  animate?: boolean;
}) {
  return (
    <div
      className="flex items-center justify-center gap-2"
      role="img"
      aria-label={`${stars} out of 3 stars`}
    >
      {[1, 2, 3].map((n) => (
        <span
          key={n}
          className={animate && n <= stars ? 'k-star-pop' : undefined}
          style={{
            fontSize: size,
            lineHeight: 1,
            animationDelay: animate ? `${(n - 1) * 180}ms` : undefined,
            filter: n <= stars ? 'none' : 'grayscale(1)',
            opacity: n <= stars ? 1 : 0.28,
          }}
          aria-hidden="true"
        >
          ⭐
        </span>
      ))}
    </div>
  );
}
